/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * The selectors for Money Transfer wizard
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.2.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IAccountSelectItem } from '../../components/AccountSelect/AccountSelectTypes';
import { IWizardValidationError } from '../../modules/wizard/wizardTypes';
import { IRootReducer } from '../../reducers/rootReducer';
import { IMoneyTransferReducer } from './moneyTransferReducer';
import { EMoneyTransferParamType, IMoneyTransferErrorsStepAccount } from './types/moneyTransferTypes';

export const makeSelectMoneyTransferState = (state: IRootReducer): IMoneyTransferReducer => state.moneyTransfer;

/*
* -------------------------------------------
*           SEARCH VALUE
* -------------------------------------------
*/

export const makeSelectMoneyTransferSearchValue = (paramType: EMoneyTransferParamType):
    OutputSelector<IRootReducer, string, (state: IMoneyTransferReducer) => string> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state[paramType].searchValue);

/*
* -------------------------------------------
*           SELECTED ACCOUNT
* -------------------------------------------
*/

export const makeSelectMoneyTransferSelectedAccount = (paramType: EMoneyTransferParamType):
    OutputSelector<IRootReducer, IAccountSelectItem, (state: IMoneyTransferReducer) => IAccountSelectItem> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state[paramType].selectedAccount);

/*
* -------------------------------------------
*           ACCOUNT ERROR CODE
* -------------------------------------------
*/

export const makeSelectMoneyTransferAccountErrorCode = (paramType: EMoneyTransferParamType):
    OutputSelector<IRootReducer, string, (state: IMoneyTransferReducer) => string> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state[paramType].errorCode);

/*
* -------------------------------------------
*           IS LOADING
* -------------------------------------------
*/

export const makeSelectMoneyTransferIsLoading = (paramType: EMoneyTransferParamType):
    OutputSelector<IRootReducer, boolean, (state: IMoneyTransferReducer) => boolean> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state[paramType].isLoading);

/*
* -------------------------------------------
*           ACCOUNT LIST
* -------------------------------------------
*/

export const makeSelectMoneyTransferAccountList = (paramType: EMoneyTransferParamType):
    OutputSelector<IRootReducer, IAccountSelectItem[], (state: IMoneyTransferReducer) => IAccountSelectItem[]> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state[paramType].accountList);

/*
* -------------------------------------------
*           ACCOUNT LIST HAS MORE
* -------------------------------------------
*/

export const makeSelectMoneyTransferHasMore = (paramType: EMoneyTransferParamType):
    OutputSelector<IRootReducer, boolean, (state: IMoneyTransferReducer) => boolean> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state[paramType].hasMore);


/*
* -------------------------------------------
*           TRANSFER AMOUNT
* -------------------------------------------
*/

export const makeSelectMoneyTransferAmount = ():
    OutputSelector<IRootReducer, string, (state: IMoneyTransferReducer) => string> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state.transferAmount.value);

/*
* -------------------------------------------
*           TRANSFER AMOUNT ERROR CODE
* -------------------------------------------
*/

export const makeSelectMoneyTransferAmountErrorCode = ():
    OutputSelector<IRootReducer, string, (state: IMoneyTransferReducer) => string> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state.transferAmount.errorCode);


/*
* -------------------------------------------
*           IS PAGE VALID
* -------------------------------------------
*/

export const makeSelectMoneyTransferPageValid = ():
    OutputSelector<IRootReducer, boolean, (state: IMoneyTransferReducer) => boolean> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state.isPageValid);

export const makeSelectMoneyTransferValidateError = ():
    OutputSelector<IRootReducer, string, (state: IMoneyTransferReducer) => string> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state.validateError);

/*
* -------------------------------------------
*           Validations online
* -------------------------------------------
*/

export const makeSelectMoneyTransferGetConflicts = ():
    OutputSelector<IRootReducer, IWizardValidationError[],
        (state: IMoneyTransferReducer) => IWizardValidationError[]> =>
    createSelector(makeSelectMoneyTransferState,
        state => state.conflicts);


/*
* -------------------------------------------
*           ERRORS STEP ACCOUNT
* -------------------------------------------
*/

export const makeSelectMoneyTransferErrorsStepAccount = ():
    OutputSelector<IRootReducer, IMoneyTransferErrorsStepAccount, (state: IMoneyTransferReducer) =>
        IMoneyTransferErrorsStepAccount> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => ({
            transferAccountFromError: state[EMoneyTransferParamType.transferAccountFrom].errorCode,
            transferAccountToError: state[EMoneyTransferParamType.transferAccountTo].errorCode,
            transferAmountError: state.transferAmount.errorCode,
        }));

/*
* -------------------------------------------
*          CHECK RECIPIENTS IS LOADING
* -------------------------------------------
*/
export const makeSelectMoneyTransferCheckRecipientsLoading = ():
    OutputSelector<IRootReducer, boolean, (state: IMoneyTransferReducer) => boolean> =>
    createSelector(makeSelectMoneyTransferState,
        (state) => state.isCheckRecipientsLoading);

